import { useEffect, useRef, useState } from 'react'
import * as THREE from 'three'
import type { FragmentsModel } from '@thatopen/fragments'
import { useAppStore } from '@core/store'
import { viewerApi } from '@viewer/lib/viewerApi'
import { ultimoDireitoFoiArrasto } from '@viewer/canvas/arrastoDireito'
import {
  cameraPosForHorizontal,
  faceHorizontalDir,
} from '@viewer/lib/bearingFromLine'
import { measureApi } from '@viewer/measure/measureApi'
import { raycastAllVisiveis, raycastVisivel } from '@viewer/lib/picking'
import { useShortcutHandler } from '@core/shortcuts'
import type { SelectionTarget } from '@viewer/store/selectionSlice'
import {
  getModelsBbox,
  orientationFromFace,
  projectAabbToLocal,
} from '@viewer/section-box/sectionMath'

// Wires canvas pointer/keyboard interactions to the selection slice:
//   • hover raycast (rAF-throttled) → setHover, paints highlighter 'hover'
//   • click with Ctrl/Shift modifiers → add/remove/replace
//   • marquee drag (when marqueeMode) → rectangleRaycast → bulk select
//   • Escape → cancel marquee → exit mode → clear selection
//
// Also paints the highlighter 'select' style from selectedTargets.

export interface MarqueeOverlayInfo {
  x: number
  y: number
  width: number
  height: number
  mode: 'window' | 'crossing'
}

export function useInteraction(
  containerRef: React.RefObject<HTMLDivElement | null>,
): {
  marqueeOverlay: MarqueeOverlayInfo | null
} {
  const [marqueeOverlay, setMarqueeOverlay] =
    useState<MarqueeOverlayInfo | null>(null)
  const dragRef = useRef<{
    button: number
    startX: number
    startY: number
    moved: boolean
    pointerId: number
  } | null>(null)
  const marqueeRef = useRef<{
    pointerId: number
    startPx: { x: number; y: number } // canvas-relative (CSS overlay)
    startClient: { x: number; y: number } // page (Fragments raycast)
  } | null>(null)
  const hoverTokenRef = useRef(0)
  const hoverScheduledRef = useRef(false)
  const lastHoverEventRef = useRef<PointerEvent | null>(null)
  const suppressClickRef = useRef(false)
  // Tab cycling: list of all elements under the ray, sorted near-to-
  // far. Tab/Shift+Tab steps the cursor through them, advancing the
  // store's hover. Reset whenever the pointer moves more than ~6px.
  const tabCycleRef = useRef<{
    targets: SelectionTarget[]
    cursor: number
    originPx: { x: number; y: number }
  } | null>(null)

  // ---- Sync selectedTargets → highlighter 'select' ----
  useEffect(() => {
    const unsubscribe = useAppStore.subscribe((state, prev) => {
      if (state.selectedTargets === prev.selectedTargets) return
      const hl = viewerApi.getHighlighter()
      if (!hl) return
      const map: Record<string, Set<number>> = {}
      for (const t of state.selectedTargets) {
        if (!map[t.modelId]) map[t.modelId] = new Set()
        map[t.modelId].add(t.localId)
      }
      void hl
        .highlightByID('select', map, true, false)
        .catch(() => {
          /* non-geometric items are fine */
        })
        // (Old paint re-application after selection used to live here:
        // necessary when paints went through Fragments' setColor and the
        // highlighter's resetColor wiped them. With paintEngine the
        // colours are in a custom shader uniform texture untouched by
        // the highlighter, so no recovery step is needed.)
      // When ghost mode is on, the previously-selected items also need
      // to be re-ghosted (and the new selection un-ghosted). Fire and
      // forget: the worst case is a one-frame stale overlay.
      void viewerApi.refreshGhost()
      // No auto-pivot on selection. camera-controls' setOrbitPoint has
      // to recompute the focal offset to keep the camera visually
      // still, and that recomputation produces a sub-frame shift the
      // user reads as a small camera jump. Pivoting on every click is
      // cosmetic: orbiting still works, just around the previous
      // pivot. For explicit "pivot here", the P key /
      // context menu calls `setOrbitAndLookAtSelection`.
    })
    return unsubscribe
  }, [])

  // ---- Sync hover → highlighter 'hover' ----
  useEffect(() => {
    const unsubscribe = useAppStore.subscribe((state, prev) => {
      if (state.hover === prev.hover) return
      const hl = viewerApi.getHighlighter()
      if (!hl) return
      if (!state.hover) {
        void hl.clear('hover').catch(() => {})
        return
      }
      const t = state.hover
      void hl
        .highlightByID(
          'hover',
          { [t.modelId]: new Set([t.localId]) },
          true,
          false,
        )
        .catch(() => {})
    })
    return unsubscribe
  }, [])

  // ---- Canvas pointer handlers ----
  useEffect(() => {
    const container = containerRef.current
    if (!container) return
    // The renderer canvas is appended inside containerRef; find it
    // each call since the ThatOpen world creates it asynchronously.

    let canvas: HTMLCanvasElement | null = null

    const findCanvas = (): HTMLCanvasElement | null => {
      if (canvas && canvas.isConnected) return canvas
      const world = viewerApi.getWorld()
      if (!world) return null
      canvas = world.renderer!.three.domElement
      return canvas
    }

    // Fragments raycast / raycastAll / rectangleRaycast all expect
    // CLIENT coordinates (event.clientX/Y); they internally subtract
    // canvas rect.left/top to derive NDC. Passing NDC ourselves
    // double-converts and returns nothing.
    const clientOf = (e: PointerEvent | MouseEvent) =>
      new THREE.Vector2(e.clientX, e.clientY)

    // Canvas-relative pixels are needed for the marquee CSS overlay.
    const pixelOf = (e: PointerEvent | MouseEvent, c: HTMLCanvasElement) => {
      const rect = c.getBoundingClientRect()
      return { x: e.clientX - rect.left, y: e.clientY - rect.top }
    }

    const isModelVisible = (m: FragmentsModel): boolean => {
      // Skip hidden models (their object.visible is toggled by the
      // sidebar's eye button) so picking matches what the user sees.
      const obj = (m as unknown as { object?: { visible: boolean } }).object
      return obj ? obj.visible : true
    }

    const raycastFirst = async (
      mouse: THREE.Vector2,
      c: HTMLCanvasElement,
    ): Promise<SelectionTarget | null> => {
      const world = viewerApi.getWorld()
      if (!world) return null
      const camera = world.camera.three as THREE.PerspectiveCamera
      let best: { dist: number; t: SelectionTarget } | null = null
      for (const [modelId, model] of viewerApi.iterModels()) {
        if (!isModelVisible(model as FragmentsModel)) continue
        // O primeiro impacto À VISTA: com a caixa de corte, o mais
        // próximo pode estar na parte cortada (ver cortesNoRaio.ts).
        const result = await raycastVisivel(model as FragmentsModel, {
          camera,
          mouse,
          dom: c,
        })
        if (result && typeof result.localId === 'number') {
          const dist = (result as { distance?: number }).distance ?? 0
          if (!best || dist < best.dist) {
            best = { dist, t: { modelId, localId: result.localId } }
          }
        }
      }
      return best?.t ?? null
    }

    const raycastAllSorted = async (
      mouse: THREE.Vector2,
      c: HTMLCanvasElement,
    ): Promise<SelectionTarget[]> => {
      const world = viewerApi.getWorld()
      if (!world) return []
      const camera = world.camera.three as THREE.PerspectiveCamera
      const all: { dist: number; target: SelectionTarget }[] = []
      for (const [modelId, model] of viewerApi.iterModels()) {
        if (!isModelVisible(model as FragmentsModel)) continue
        // Sem os impactos na parte cortada: eram eles que faziam a
        // pré-selecção acender (sem se ver) um elemento escondido fora
        // da caixa, à frente do que o utilizador tinha debaixo do cursor.
        const results = await raycastAllVisiveis(model as FragmentsModel, {
          camera,
          mouse,
          dom: c,
        })
        for (const r of results) {
          if (typeof r.localId !== 'number') continue
          const dist = (r as { distance?: number }).distance ?? 0
          all.push({ dist, target: { modelId, localId: r.localId } })
        }
      }
      all.sort((a, b) => a.dist - b.dist)
      // Dedupe: same modelId+localId may appear if Fragments returns
      // multiple sub-mesh hits for the same item.
      const seen = new Set<string>()
      const out: SelectionTarget[] = []
      for (const e of all) {
        const k = `${e.target.modelId}:${e.target.localId}`
        if (seen.has(k)) continue
        seen.add(k)
        out.push(e.target)
      }
      return out
    }

    const runHover = async () => {
      const e = lastHoverEventRef.current
      lastHoverEventRef.current = null
      hoverScheduledRef.current = false
      if (!e) return
      const c = findCanvas()
      if (!c) return
      const state = useAppStore.getState()
      if (state.marqueeMode || dragRef.current?.moved) {
        if (state.hover) state.setHover(null)
        tabCycleRef.current = null
        return
      }
      const myToken = ++hoverTokenRef.current
      const targets = await raycastAllSorted(clientOf(e), c)
      if (myToken !== hoverTokenRef.current) return // stale
      const px = pixelOf(e, c)
      tabCycleRef.current =
        targets.length > 0
          ? { targets, cursor: 0, originPx: px }
          : null
      const cur = useAppStore.getState()
      const target = targets[0] ?? null
      if (target) {
        if (
          !cur.hover ||
          cur.hover.modelId !== target.modelId ||
          cur.hover.localId !== target.localId
        ) {
          cur.setHover(target)
        }
      } else if (cur.hover) {
        cur.setHover(null)
      }
    }

    const onPointerMove = (e: PointerEvent) => {
      const c = findCanvas()
      if (!c) return
      const state = useAppStore.getState()
      const px = pixelOf(e, c)
      state.setHoverCursor(px)

      // Don't run the hover raycast while a measurement tool is
      // capturing the canvas; the measurement preview already shows
      // the snap target and a dual highlight is just noise.
      //
      // O mesmo enquanto se traça a linha de alinhamento: os elementos
      // a acender sob o cursor faziam o gesto parecer uma selecção, e
      // era essa a queixa. Quem está a traçar uma direcção não está a
      // escolher elementos.
      if (state.measureMode !== null || state.bearingPick) {
        if (state.hover) state.setHover(null)
        return
      }

      // Marquee drag in progress?
      if (marqueeRef.current && e.pointerId === marqueeRef.current.pointerId) {
        const m = marqueeRef.current
        const dx = px.x - m.startPx.x
        const dy = px.y - m.startPx.y
        // Below the click threshold we keep the overlay hidden so a
        // jittery click on empty space doesn't flash a tiny marquee.
        if (Math.abs(dx) < 4 && Math.abs(dy) < 4) {
          return
        }
        const x = Math.min(m.startPx.x, px.x)
        const y = Math.min(m.startPx.y, px.y)
        const mode: 'window' | 'crossing' = dx >= 0 ? 'window' : 'crossing'
        setMarqueeOverlay({
          x,
          y,
          width: Math.abs(dx),
          height: Math.abs(dy),
          mode,
        })
        return
      }

      // Plain orbit / drag (left button held with no marquee mode):
      // mark moved so click is suppressed.
      if (dragRef.current && e.buttons !== 0) {
        const totalDx = Math.abs(e.clientX - dragRef.current.startX)
        const totalDy = Math.abs(e.clientY - dragRef.current.startY)
        if (totalDx > 4 || totalDy > 4) dragRef.current.moved = true
      }

      // Hover raycast (rAF-throttled, only when idle).
      lastHoverEventRef.current = e
      if (!hoverScheduledRef.current) {
        hoverScheduledRef.current = true
        requestAnimationFrame(runHover)
      }
    }

    const onPointerDown = (e: PointerEvent) => {
      if (e.button !== 0) return
      // Touch: ignore secondary contacts (the second finger of a pinch or
      // two-finger pan). Only the primary pointer drives selection; the
      // camera-controls library handles the multi-touch gestures itself.
      const isTouch = e.pointerType !== 'mouse'
      if (isTouch && !e.isPrimary) return
      const c = findCanvas()
      if (!c) return
      // While a measurement tool is active, the LengthMeasurement /
      // AreaMeasurement components own the canvas clicks. Skip the
      // selection / marquee path entirely; otherwise the user would
      // both place a measurement point AND change the selection on
      // the same click.
      const sNow = useAppStore.getState()
      if (sNow.measureMode !== null) return
      // Same for the work-plane face picker: useWorkPlane handles
      // the click in capture phase to set the plane.
      if (sNow.workPlanePickMode) return
      dragRef.current = {
        button: e.button,
        startX: e.clientX,
        startY: e.clientY,
        moved: false,
        pointerId: e.pointerId,
      }
      const state = useAppStore.getState()
      // Auto-marquee: when the user starts a drag on empty space (no
      // hover under the cursor), pre-arm a marquee. Camera-controls are
      // disabled now so the orbit doesn't fight the marquee. If the
      // user just clicks (no drag past the threshold), we'll re-enable
      // them in onPointerUp and treat it as a regular empty-area click
      // (which clears the selection). Holding modifiers during the
      // marquee adds/removes from selection like in Revit.
      //
      // On touch the auto-marquee is OFF: there's no hover before the
      // touch, so every one-finger drag would otherwise start a marquee
      // and steal the orbit gesture. One finger orbits (camera-controls
      // touches.one); a marquee is opt-in via the "Janela" mode toggle.
      const startMarquee =
        state.marqueeMode || (!isTouch && state.hover === null)
      if (startMarquee) {
        e.preventDefault()
        const px = pixelOf(e, c)
        marqueeRef.current = {
          pointerId: e.pointerId,
          startPx: px,
          startClient: { x: e.clientX, y: e.clientY },
        }
        // Don't paint the overlay yet, only once the drag exceeds
        // the click threshold. Otherwise empty-area clicks would
        // briefly flash a 0×0 box.
        c.setPointerCapture?.(e.pointerId)
        const world = viewerApi.getWorld()
        if (world) world.camera.controls.enabled = false
      }
    }

    const onPointerUp = (e: PointerEvent) => {
      const c = findCanvas()
      if (!c) return
      // Marquee complete?
      if (marqueeRef.current && e.pointerId === marqueeRef.current.pointerId) {
        const m = marqueeRef.current
        marqueeRef.current = null
        // Use canvas-pixel coords for the size sanity check and the
        // direction (window vs crossing); use CLIENT coords for the
        // Fragments rectangleRaycast call.
        const pxEnd = pixelOf(e, c)
        const dx = pxEnd.x - m.startPx.x
        const dy = pxEnd.y - m.startPx.y
        const fullyIncluded = dx >= 0 // left→right = window
        const world = viewerApi.getWorld()
        if (world) world.camera.controls.enabled = true
        setMarqueeOverlay(null)
        c.releasePointerCapture?.(e.pointerId)
        if (Math.abs(dx) > 4 && Math.abs(dy) > 4) {
          // Real drag: run bulk select and swallow the click.
          suppressClickRef.current = true
          const tlClient = new THREE.Vector2(
            Math.min(m.startClient.x, e.clientX),
            Math.min(m.startClient.y, e.clientY),
          )
          const brClient = new THREE.Vector2(
            Math.max(m.startClient.x, e.clientX),
            Math.max(m.startClient.y, e.clientY),
          )
          void runMarqueeBulk(
            tlClient,
            brClient,
            fullyIncluded,
            e.shiftKey,
            e.ctrlKey || e.metaKey,
          )
          return
        }
        // No drag: fall through to plain click handling so an empty
        // click still clears the selection.
      }

      // Plain click (no marquee).
      const drag = dragRef.current
      dragRef.current = null
      if (!drag) return
      const totalDx = Math.abs(e.clientX - drag.startX)
      const totalDy = Math.abs(e.clientY - drag.startY)
      // Movement tolerance below which the gesture counts as a click/tap
      // rather than an orbit. Touch needs a larger window than a mouse
      // because a fingertip wobbles more than a cursor.
      const tol = e.pointerType === 'mouse' ? 4 : 10
      if (totalDx > tol || totalDy > tol) return
      void handleCanvasClick(e, c)
    }

    const handleCanvasClick = async (e: PointerEvent, c: HTMLCanvasElement) => {
      if (suppressClickRef.current) {
        suppressClickRef.current = false
        return
      }
      const state = useAppStore.getState()
      // Section box "Alinhar à face" mode: the next click picks a
      // surface; we orient the OBB so axisZ matches its normal and
      // the origin snaps to the hit point.
      if (state.sectionAlignMode) {
        await handleAlignClick(clientOf(e), c)
        return
      }
      // À espera de uma face para criar um plano de corte paralelo a
      // ela. É isto o "rodar graficamente" que serve para alguma coisa:
      // uma fachada oblíqua não tem ângulo redondo nenhum, e apanhá-lo
      // arrastando um manípulo é trabalho perdido.
      if (state.planoPorFace) {
        await handlePlanoPorFace(clientOf(e), c)
        return
      }
      // A traçar a linha que define a horizontal do ecrã: dois cliques
      // sobre o modelo e a direcção entre eles fica horizontal nas
      // vistas padrão. Roda o SIGNIFICADO das vistas, nunca o modelo.
      if (state.bearingPick) {
        await handleBearingClick(clientOf(e), c)
        return
      }
      // Trust the current hover state instead of re-raycasting. The
      // hover is rAF-throttled and reflects exactly what the user
      // sees highlighted; doing a fresh raycast at click time can
      // pick a different element when the section box, multi-model
      // ordering or floating-point distance ties produce a different
      // first hit. "Click selects what you're hovering" matches
      // intuition.
      //
      // Touch has no hover (the finger only touches at tap time), so
      // raycast at the tapped point to find the target.
      let target = state.hover
      if (!target && e.pointerType !== 'mouse') {
        target = await raycastFirst(clientOf(e), c)
      }
      if (!target) {
        // Nada dos modelos debaixo do cursor. Antes de limpar, pergunta
        // ao IFC5: as malhas dele são `THREE.Mesh` normais e não
        // aparecem no raio dos Fragments, portanto um clique numa
        // parede IFC5 chegava aqui como "clicou no vazio".
        const { ifcxApi } = await import('@viewer/ifcx/ifcxApi')
        if (ifcxApi.picar(e.clientX, e.clientY, c)) return
        if (!e.ctrlKey && !e.metaKey && !e.shiftKey) {
          state.clearSelection()
        }
        return
      }
      // Apanhou um elemento de um modelo: o que estivesse escolhido no
      // IFC5 deixa de estar, senão ficavam dois realces acesos e duas
      // coisas a dizerem-se seleccionadas.
      void import('@viewer/ifcx/ifcxApi').then(({ ifcxApi }) =>
        ifcxApi.limparSeleccao(),
      )
      if (e.shiftKey) {
        state.removeFromSelection(target)
      } else if (e.ctrlKey || e.metaKey) {
        state.toggleSelection(target)
      } else {
        state.setSelection(target)
      }
    }

    /** Alinhar a vista por uma FACE clicada.
     *
     *  Um clique, não dois. A normal da face dá a direcção exacta da
     *  parede, igual em toda a superfície, portanto não depende de onde
     *  se clicou nem precisa de snap. Dois pontos apanhados à mão sobre
     *  a mesma parede davam a mesma direcção com erro, tanto maior
     *  quanto mais perto estivessem um do outro. */
    const handleBearingClick = async (
      mouse: THREE.Vector2,
      c: HTMLCanvasElement,
    ) => {
      const world = viewerApi.getWorld()
      if (!world) return
      const camera = world.camera.three as THREE.PerspectiveCamera
      let best: {
        dist: number
        point: THREE.Vector3
        normal: THREE.Vector3
      } | null = null
      for (const [, model] of viewerApi.iterModels()) {
        if (!isModelVisible(model as FragmentsModel)) continue
        const result = await raycastVisivel(model as FragmentsModel, {
          camera,
          mouse,
          dom: c,
        })
        if (!result) continue
        const point = (result as { point?: THREE.Vector3 }).point
        const normal = (result as { normal?: THREE.Vector3 }).normal
        const dist = (result as { distance?: number }).distance ?? 0
        if (point && normal && (!best || dist < best.dist)) {
          best = { dist, point, normal }
        }
      }
      const state = useAppStore.getState()
      if (!best) {
        state.pushToast('Clica sobre uma face do modelo para alinhar.', {
          type: 'info',
        })
        return
      }

      const dir = faceHorizontalDir([
        best.normal.x,
        best.normal.y,
        best.normal.z,
      ])
      if (!dir) {
        state.pushToast(
          'Essa superfície é horizontal e não define direcção. Clica ' +
            'numa parede ou fachada.',
          { type: 'info' },
        )
        return
      }

      const cam = world.camera.three
      const alvo = new THREE.Vector3()
      world.camera.controls.getTarget(alvo)
      const r = cameraPosForHorizontal({
        target: [alvo.x, alvo.y, alvo.z],
        camera: [cam.position.x, cam.position.y, cam.position.z],
        a: [0, 0, 0],
        b: dir,
      })
      state.setBearingPick(null)
      if (!r) return

      void world.camera.controls.setLookAt(
        r.pos[0],
        r.pos[1],
        r.pos[2],
        alvo.x,
        alvo.y,
        alvo.z,
        true,
      )
      // O cubo passa a mostrar a face alinhada com a fachada escolhida.
      // O ângulo vem JUNTO com a posição, na mesma convenção: recalculá-lo
      // à parte foi o que antes o fez apontar para o sítio errado.
      state.setDisplayBearing(r.azimuthDeg)
      state.pushToast(
        'Vista alinhada com a fachada. O modelo e a georreferenciação ' +
          'não foram tocados.',
        { type: 'success' },
      )
    }

    const handleAlignClick = async (
      mouse: THREE.Vector2,
      c: HTMLCanvasElement,
    ) => {
      const world = viewerApi.getWorld()
      if (!world) return
      const camera = world.camera.three as THREE.PerspectiveCamera
      // Raycast across visible models; pick closest hit with normal.
      let best: { dist: number; point: THREE.Vector3; normal: THREE.Vector3 } | null =
        null
      for (const [, model] of viewerApi.iterModels()) {
        if (!isModelVisible(model as FragmentsModel)) continue
        const result = await raycastVisivel(model as FragmentsModel, {
          camera,
          mouse,
          dom: c,
        })
        if (!result) continue
        const point = (result as { point?: THREE.Vector3 }).point
        const normal = (result as { normal?: THREE.Vector3 }).normal
        const dist = (result as { distance?: number }).distance ?? 0
        if (point && normal && (!best || dist < best.dist)) {
          best = { dist, point, normal }
        }
      }
      const state = useAppStore.getState()
      // Always exit align mode after the click attempt.
      state.setSectionAlignMode(false)
      if (!best) return
      const orientation = orientationFromFace(best.point, best.normal)
      if (!orientation) return
      const modelBox = getModelsBbox()
      if (!modelBox) return
      const newExtents = projectAabbToLocal(orientation, modelBox)
      state.setSectionOrientation(orientation)
      state.setSectionExtents(newExtents)
      state.setSectionExtentRange(newExtents)
    }

    /** Cria um plano de corte paralelo à face clicada. */
    const handlePlanoPorFace = async (
      mouse: THREE.Vector2,
      c: HTMLCanvasElement,
    ) => {
      const world = viewerApi.getWorld()
      const state = useAppStore.getState()
      // Sai do modo aconteça o que acontecer: ficar à espera depois de
      // um clique falhado faz o gesto seguinte criar um corte que
      // ninguém pediu.
      state.setPlanoPorFace(false)
      if (!world) return
      const camera = world.camera.three as THREE.PerspectiveCamera
      let best: { dist: number; point: THREE.Vector3; normal: THREE.Vector3 } | null =
        null
      for (const [, model] of viewerApi.iterModels()) {
        if (!isModelVisible(model as FragmentsModel)) continue
        const result = await raycastVisivel(model as FragmentsModel, {
          camera,
          mouse,
          dom: c,
        })
        if (!result) continue
        const point = (result as { point?: THREE.Vector3 }).point
        const normal = (result as { normal?: THREE.Vector3 }).normal
        const dist = (result as { distance?: number }).distance ?? 0
        if (point && normal && (!best || dist < best.dist)) {
          best = { dist, point, normal }
        }
      }
      if (!best) return
      const { daFace } = await import('@viewer/section-box/planosDeCorte')
      const quantos = state.planosDeCorte.filter((p) =>
        p.nome.startsWith('Corte pela face'),
      ).length
      const plano = daFace(
        [best.normal.x, best.normal.y, best.normal.z],
        [best.point.x, best.point.y, best.point.z],
        crypto.randomUUID(),
        quantos + 1,
      )
      if (plano) state.acrescentarPlano(plano)
    }

    const runMarqueeBulk = async (
      topLeft: THREE.Vector2,
      bottomRight: THREE.Vector2,
      fullyIncluded: boolean,
      shift: boolean,
      ctrl: boolean,
    ) => {
      const c = findCanvas()
      if (!c) return
      const world = viewerApi.getWorld()
      if (!world) return
      const camera = world.camera.three as THREE.PerspectiveCamera
      const collected: SelectionTarget[] = []
      for (const [modelId, model] of viewerApi.iterModels()) {
        if (!isModelVisible(model as FragmentsModel)) continue
        const result = await (model as FragmentsModel)
          .rectangleRaycast({
            camera,
            dom: c,
            topLeft,
            bottomRight,
            fullyIncluded,
          })
          .catch(() => null)
        if (result && Array.isArray(result.localIds)) {
          for (const id of result.localIds) {
            collected.push({ modelId, localId: id })
          }
        }
      }
      const mode: 'replace' | 'add' | 'remove' = shift
        ? 'remove'
        : ctrl
          ? 'add'
          : 'replace'
      useAppStore.getState().applyBulkSelection(collected, mode)
    }

    const onPointerLeave = () => {
      const state = useAppStore.getState()
      if (state.hover) state.setHover(null)
      state.setHoverCursor(null)
    }

    const onDoubleClick = async (e: MouseEvent) => {
      const c = findCanvas()
      if (!c) return
      e.preventDefault()
      e.stopPropagation()
      const target = await raycastFirst(clientOf(e), c)
      if (!target) return
      // setOrbitToElement keeps the camera in place and only repins
      // the pivot. The element's bbox centre becomes the new orbit
      // axis through which all subsequent rotations happen.
      void viewerApi.setOrbitToElement(target.modelId, target.localId)
    }

    const onContextMenu = async (e: MouseEvent) => {
      const c = findCanvas()
      if (!c) return
      e.preventDefault()
      e.stopPropagation()
      // Arrastar com o direito roda a câmara. No Windows o browser
      // dispara este evento ao largar o botão, também depois de um
      // arrasto, e sem esta pergunta o menu abria no fim de cada
      // rotação.
      if (ultimoDireitoFoiArrasto()) return
      const target = await raycastFirst(clientOf(e), c)
      const state = useAppStore.getState()
      // Standard CAD behaviour: right-click on an unselected element
      // promotes it to the active selection so the menu's actions
      // operate on the right thing. Right-click on the empty canvas
      // leaves the existing selection alone.
      if (target) {
        const already = state.selectedTargets.some(
          (t) =>
            t.modelId === target.modelId && t.localId === target.localId,
        )
        if (!already) state.setSelection(target)
      }
      state.openContextMenu({
        x: e.clientX,
        y: e.clientY,
        target,
      })
    }

    // We don't yet have a canvas reference; we listen on the
    // container. Pointer events on the canvas bubble up.
    container.addEventListener('pointermove', onPointerMove)
    container.addEventListener('pointerdown', onPointerDown)
    container.addEventListener('pointerup', onPointerUp)
    container.addEventListener('pointercancel', onPointerUp)
    container.addEventListener('pointerleave', onPointerLeave)
    container.addEventListener('dblclick', onDoubleClick)
    container.addEventListener('contextmenu', onContextMenu)

    return () => {
      container.removeEventListener('pointermove', onPointerMove)
      container.removeEventListener('pointerdown', onPointerDown)
      container.removeEventListener('pointerup', onPointerUp)
      container.removeEventListener('pointercancel', onPointerUp)
      container.removeEventListener('pointerleave', onPointerLeave)
      container.removeEventListener('dblclick', onDoubleClick)
      container.removeEventListener('contextmenu', onContextMenu)
    }
  }, [containerRef])

  // ---- Shortcut handler registration ----
  // Each action has a stable ID; the dispatcher in core/shortcuts owns
  // the keydown listener and decides what fires. Refactoring out of an
  // inline if-chain lets the user rebind these via the help modal.
  useShortcutHandler('viewer.focusSelection', () => {
    void viewerApi.focusOnSelection()
  })
  useShortcutHandler('viewer.setPivotHere', () => {
    void viewerApi.setOrbitAndLookAtSelection()
  })
  useShortcutHandler('viewer.hideSelected', () => {
    void viewerApi.hideSelected()
  })
  useShortcutHandler('viewer.isolateSelected', () => {
    void viewerApi.isolateSelected()
  })
  useShortcutHandler('viewer.invertSelection', () => {
    void viewerApi.invertSelection()
  })
  useShortcutHandler('viewer.showAll', () => {
    void viewerApi.showAll()
  })
  useShortcutHandler('viewer.toggleGhost', () => {
    const cur = useAppStore.getState().ghostMode
    void viewerApi.setGhostMode(!cur)
  })
  useShortcutHandler('viewer.toggleGrid', () => {
    const cur = useAppStore.getState().gridVisible
    viewerApi.setGridVisible(!cur)
  })
  useShortcutHandler('viewer.toggleEdges', () => {
    const cur = useAppStore.getState().edgesVisible
    viewerApi.setEdgesVisible(!cur)
  })
  useShortcutHandler('viewer.toggleClassColors', () => {
    void (async () => {
      const cur = useAppStore.getState().classColorsEnabled
      useAppStore.getState().setClassColorsEnabled(!cur)
      await viewerApi.repaintAllModels({ reset: true })
    })()
  })
  useShortcutHandler('viewer.toggleBgMode', () => {
    const cur = useAppStore.getState().bgMode
    void viewerApi.setBgMode(cur === 'light' ? 'dark' : 'light')
  })
  useShortcutHandler('viewer.toggleHideSpaces', () => {
    const cur = useAppStore.getState().hideSpatialVolumes
    void viewerApi.setSpatialVolumesHidden(!cur)
  })
  useShortcutHandler('viewer.toggleAppearancePanel', () => {
    const cur = useAppStore.getState().appearancePanelOpen
    useAppStore.getState().setAppearancePanelOpen(!cur)
  })
  useShortcutHandler('viewer.toggleColorByPanel', () => {
    const cur = useAppStore.getState().colorByPanelOpen
    useAppStore.getState().setColorByPanelOpen(!cur)
  })
  useShortcutHandler('viewer.toggleFilterPanel', () => {
    const cur = useAppStore.getState().filterPanelOpen
    useAppStore.getState().setFilterPanelOpen(!cur)
  })
  useShortcutHandler('viewer.selectAllOfClass', () => {
    const sel = useAppStore.getState().selection
    if (!sel) return
    void viewerApi.selectAllOfClass(sel.modelId, sel.localId)
  })
  useShortcutHandler('viewer.selectAllOfSameType', () => {
    const sel = useAppStore.getState().selection
    if (!sel) return
    void viewerApi.selectAllOfSameType(sel.modelId, sel.localId)
  })
  useShortcutHandler('viewer.selectAllInStorey', () => {
    const sel = useAppStore.getState().selection
    if (!sel) return
    void viewerApi.selectAllInSpatialContainer(
      sel.modelId,
      sel.localId,
      'storey',
    )
  })
  useShortcutHandler('viewer.selectAllInBuilding', () => {
    const sel = useAppStore.getState().selection
    if (!sel) return
    void viewerApi.selectAllInSpatialContainer(
      sel.modelId,
      sel.localId,
      'building',
    )
  })
  // Tab cycling: needs access to the local tab cycle ref, which only
  // lives inside this hook. The dispatcher just wakes us up. Returns
  // false when there is nothing to cycle, so Tab keeps moving the focus.
  const tabCycle = (step: 1 | -1): boolean => {
    const cycle = tabCycleRef.current
    if (!cycle || cycle.targets.length <= 1) return false
    cycle.cursor =
      (cycle.cursor + step + cycle.targets.length) % cycle.targets.length
    useAppStore.getState().setHover(cycle.targets[cycle.cursor])
    return true
  }
  useShortcutHandler('viewer.tabCycleNext', () => tabCycle(1))
  useShortcutHandler('viewer.tabCyclePrev', () => tabCycle(-1))
  // Escape cascade: section align → marquee → annotation/measure modes
  // → marquee mode → selection. The shortcut dispatcher swallows
  // Escape (capture phase + stopPropagation), so this handler is the
  // single source of truth for what Esc does.
  useShortcutHandler('viewer.clearOrCancel', () => {
    const state = useAppStore.getState()
    if (state.sectionAlignMode) {
      state.setSectionAlignMode(false)
      return
    }
    if (marqueeRef.current) {
      const world = viewerApi.getWorld()
      // We don't have a direct canvas ref here, but pointer capture is
      // released next pointerup; setting marquee to null + re-enabling
      // controls is enough.
      marqueeRef.current = null
      setMarqueeOverlay(null)
      if (world) world.camera.controls.enabled = true
      return
    }
    if (state.coordsOriginPickMode) {
      state.setCoordsOriginPickMode(false)
      return
    }
    if (state.workPlanePickMode) {
      state.setWorkPlanePickMode(false)
      return
    }
    if (state.annotationPlaceMode) {
      // Coord place mode also has the live HUD + 3D cursor crosshair
      // toggled on. Clearing only the place mode leaves those visual
      // remnants behind, so clear them together. The 3D cursor follows
      // showCoordinates inside useCoordsMarkers; turning that off
      // hides the marker.
      if (state.annotationPlaceMode === 'coord') {
        state.setShowCoordinates(false)
      }
      state.setAnnotationPlaceMode(null)
      state.setAnnotationDraftFirstPoint(null)
      return
    }
    if (state.measureDeleteMode) {
      state.setMeasureDeleteMode(false)
      return
    }
    if (state.measureMode !== null) {
      // Cancel any in-progress measurement (partial polygon, half-drawn
      // dimension) before exiting the mode entirely.
      measureApi.cancelInProgress()
      state.setMeasureMode(null)
      return
    }
    if (state.marqueeMode) {
      state.setMarqueeMode(false)
      return
    }
    if (state.selectedTargets.length > 0) state.clearSelection()
  })

  return { marqueeOverlay }
}
